import React from 'react';
import { Alert, Button, Progress, Tooltip } from 'antd';
import { DollarOutlined, TeamOutlined, ClockCircleOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { reportMoney, reportNumber } from './hrReportsApi';

export interface DashboardData {
  summary: { total_employees: number; active_employees: number; inactive_employees: number; terminated_employees: number;
    cadres: number; non_cadres: number; male_count: number; female_count: number };
  departments: Array<{ id: number; name: string; code: string; budget_annual: number; employee_count: number;
    active_employees: number; avg_salary: number; total_salary_cost: number }>;
  evolution: Array<{ month: string; period_name: string; employee_count: number; total_gross_salary: number; total_net_salary: number }>;
  ageDistribution: Array<{ age_range: string; count: number }>;
  recentActivities: Array<{ type: string; description: string; date: string; status: string }>;
  generatedAt: string;
  // Optional values are displayed only if supplied by the server. Never estimate them from headcount.
  attendance?: { presentToday?: number; absentToday?: number; lateToday?: number; averageHours?: number };
  payroll?: { pendingPayments?: number; complianceScore?: number };
}

interface Props { data?: DashboardData; period: string; complianceScore?: number; auditAvailable?: boolean; onAuditOpen?: () => void }

export default function RealTimeStats({ data, period, complianceScore, auditAvailable, onAuditOpen }: Props) {
  const payroll = data ? [...data.evolution].reverse().find(row => row.month.slice(0, 7) === period) : undefined;
  const total = reportNumber(data?.summary.total_employees);
  const active = reportNumber(data?.summary.active_employees);
  const present = reportNumber(data?.attendance?.presentToday);
  const score = reportNumber(complianceScore ?? data?.payroll?.complianceScore);
  const pending = reportNumber(data?.payroll?.pendingPayments);
  const ratio = (value?: number, maximum?: number) => value !== undefined && maximum ? Math.min(100, value / maximum * 100) : 0;
  return <>
    <section className="hr-reports-metrics" aria-label="Indicateurs RH de l’entreprise">
      <div className="hr-reports-metric hr-reports-metric--salary">
        <DollarOutlined className="hr-reports-metric-icon" />
        <div><span className="hr-reports-label">Masse salariale</span>
          <strong>{reportMoney(payroll?.total_gross_salary)}</strong><small>Paie de {period.split('-').reverse().join('/')}</small></div>
      </div>
      <div className="hr-reports-metric hr-reports-metric--employees">
        <TeamOutlined className="hr-reports-metric-icon" /><div><span className="hr-reports-label">Employés actifs</span>
          <strong>{active ?? '—'} <span>/ {total ?? '—'}</span></strong>
          {active !== undefined && total !== undefined && <Progress percent={ratio(active, total)} showInfo={false} strokeColor="var(--hr-action)" size="small" />}
          <small>Effectif actuel de l’entreprise</small></div>
      </div>
      <div className="hr-reports-metric hr-reports-metric--attendance">
        <ClockCircleOutlined className="hr-reports-metric-icon" /><div><span className="hr-reports-label">Présents aujourd’hui</span>
          <strong>{present ?? '—'}{present !== undefined && <span> / {active ?? '—'}</span>}</strong>
          <small>{present === undefined ? 'Donnée de présence non disponible' : `${data?.attendance?.absentToday ?? '—'} absent(s) · ${data?.attendance?.lateToday ?? '—'} retard(s)`}</small></div>
      </div>
      <div className="hr-reports-metric hr-reports-metric--compliance">
        <SafetyCertificateOutlined className="hr-reports-metric-icon" /><div><span className="hr-reports-label">Indice de conformité</span>
          <strong>{score === undefined ? '—' : `${score} %`}</strong>
          {score !== undefined ? <Progress percent={score} showInfo={false} strokeColor="var(--hr-success)" size="small" /> : auditAvailable ?
            <Button type="link" size="small" onClick={onAuditOpen}>Consulter les scores de l’audit</Button> :
            <Tooltip title="Générez le rapport d’audit pour consulter les scores de conformité calculés."><small>Disponible après un audit</small></Tooltip>}</div>
      </div>
    </section>
    {pending !== undefined && pending > 0 && <Alert showIcon type="warning" className="hr-reports-pending" message={`${pending} paiement(s) en attente`} />}
  </>;
}
